import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toCsv, TimeSeriesChart, type ChartSeries } from '../components/TimeSeriesChart';
import { Button, Card, cx, Empty, ErrorNote, inputClass, Loading } from '../components/ui';
import type { Datapoint } from '../lib/api';
import { datapointLabel, fmtTime, fromZonedInput, humanize, toZonedInput, valueLabels } from '../lib/format';
import { useDatapoints, useHistory } from '../lib/queries';
import { usePlantContext } from './Plant';

const PRESETS = [
  { key: '1h', label: '1 h', ms: 3_600_000 },
  { key: '6h', label: '6 h', ms: 6 * 3_600_000 },
  { key: '24h', label: '24 h', ms: 86_400_000 },
  { key: '7d', label: '7 d', ms: 7 * 86_400_000 },
  { key: '30d', label: '30 d', ms: 30 * 86_400_000 },
  { key: '1y', label: '1 y', ms: 365 * 86_400_000 },
] as const;
const MAX_SELECTED = 8;

const RESOLUTION_TEXT: Record<string, string> = {
  raw: 'raw samples', '1m': '1-minute', '5m': '5-minute', '15m': '15-minute', '1h': 'hourly', '6h': '6-hour', '1d': 'daily (UTC days)',
};

/** Range and selection live in the URL, so a chart can be shared as a link. */
function useChartState() {
  const [params, setParams] = useSearchParams();
  const selected = (params.get('dp') ?? '').split(',').filter(Boolean);
  const preset = PRESETS.find((p) => p.key === params.get('range'));
  const from = Number(params.get('from'));
  const to = Number(params.get('to'));
  const custom = !preset && from > 0 && to > from ? { from, to } : null;
  const range = custom ?? { preset: preset ?? PRESETS[2] };
  const update = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null) p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { replace: true });
  };
  return { selected, range, update };
}

export function PlantCharts() {
  const plant = usePlantContext();
  const datapoints = useDatapoints(plant.slug);
  const { selected, range, update } = useChartState();
  const [filter, setFilter] = useState('');

  const now = Date.now();
  const live = 'preset' in range;
  const from = live ? now - range.preset.ms : range.from;
  const to = live ? now : range.to;
  const history = useHistory(plant.slug, selected, from, to, live, 800);

  const byId = useMemo(() => new Map((datapoints.data ?? []).map((d) => [String(d.id), d])), [datapoints.data]);
  const series: ChartSeries[] = useMemo(() => {
    if (!history.data) return [];
    return history.data.series.map((s) => {
      const d = byId.get(String(s.datapointId));
      return { label: d ? `${humanize(d.asset)}: ${datapointLabel(d)}` : s.topic, unit: s.unit, t: s.t, v: s.v, labels: d ? valueLabels(d) : null };
    });
  }, [history.data, byId]);

  if (datapoints.isPending) return <Loading />;
  if (datapoints.error) return <ErrorNote error={datapoints.error} />;

  const toggle = (id: string) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id].slice(-MAX_SELECTED);
    update({ dp: next.length ? next.join(',') : null });
  };
  const setCustom = (a: number, b: number) => update({ range: null, from: String(Math.round(a)), to: String(Math.round(b)) });

  const groups = new Map<string, Datapoint[]>();
  for (const d of datapoints.data) {
    if (d.category === 'alarm') continue;
    if (filter && !`${d.asset} ${datapointLabel(d)} ${d.topic}`.toLowerCase().includes(filter.toLowerCase())) continue;
    groups.set(d.asset, [...(groups.get(d.asset) ?? []), d]);
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
      <Card className="max-h-[70vh] overflow-y-auto p-3 lg:sticky lg:top-4 lg:self-start">
        <input className={cx(inputClass, 'mb-3')} placeholder="Find a datapoint" value={filter} onChange={(e) => setFilter(e.target.value)} />
        {[...groups.entries()].map(([asset, dps]) => (
          <div key={asset} className="mb-3">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">{humanize(asset)}</div>
            {dps.map((d) => (
              <label key={d.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-sunken" title={d.topic}>
                <input type="checkbox" checked={selected.includes(String(d.id))} onChange={() => toggle(String(d.id))} className="accent-[var(--accent)]" />
                <span className="truncate">{datapointLabel(d)}</span>
                {d.unit && <span className="ml-auto text-xs text-muted">{d.unit}</span>}
              </label>
            ))}
          </div>
        ))}
        {selected.length >= MAX_SELECTED && <p className="text-xs text-muted">Up to {MAX_SELECTED} at a time.</p>}
      </Card>

      <div className="min-w-0">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border border-rule bg-surface p-0.5">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => update({ range: p.key, from: null, to: null })}
                className={cx('rounded px-2.5 py-1 text-sm', live && range.preset.key === p.key ? 'bg-accent-soft font-medium' : 'text-muted hover:text-ink')}
              >
                {p.label}
              </button>
            ))}
          </div>
          <CustomRange from={from} to={to} timezone={plant.timezone} onApply={setCustom} />
          {!live && <Button variant="ghost" onClick={() => update({ range: '24h', from: null, to: null })}>Back to live</Button>}
          <div className="ml-auto flex items-center gap-2">
            {series.length > 0 && (
              <Button
                onClick={() => {
                  const blob = new Blob([toCsv(series, plant.timezone)], { type: 'text/csv' });
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = `${plant.slug}_${new Date(from).toISOString().slice(0, 16)}_${new Date(to).toISOString().slice(0, 16)}.csv`.replace(/:/g, '');
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}
              >
                Download CSV
              </Button>
            )}
          </div>
        </div>

        {selected.length === 0 ? (
          <Empty>Pick datapoints on the left. Drag across a chart to zoom in.</Empty>
        ) : history.error ? (
          <ErrorNote error={history.error} />
        ) : !history.data ? (
          <Loading />
        ) : (
          <Card className={cx('p-3 transition-opacity', history.isFetching && 'opacity-70')}>
            <TimeSeriesChart series={series} from={from} to={to} timezone={plant.timezone} onZoom={setCustom} />
            <p className="mt-2 text-xs text-muted">
              {fmtTime(from, plant.timezone)} – {fmtTime(to, plant.timezone)} · {RESOLUTION_TEXT[history.data.resolution]}
              {history.data.resolution !== 'raw' && ' averages (last value for states)'}
              {history.data.truncated && ' · cut at the point limit, zoom in for more'}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}

function CustomRange({ from, to, timezone, onApply }: { from: number; to: number; timezone: string; onApply: (a: number, b: number) => void }) {
  const [open, setOpen] = useState(false);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  if (!open) {
    return (
      <Button variant="ghost" onClick={() => { setA(toZonedInput(from, timezone)); setB(toZonedInput(to, timezone)); setOpen(true); }}>
        Custom…
      </Button>
    );
  }
  return (
    <form
      className="flex flex-wrap items-center gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        const start = fromZonedInput(a, timezone);
        const end = fromZonedInput(b, timezone);
        if (end > start) {
          onApply(start, end);
          setOpen(false);
        }
      }}
    >
      <input type="datetime-local" className={cx(inputClass, 'w-auto')} value={a} onChange={(e) => setA(e.target.value)} required />
      <span className="text-muted">–</span>
      <input type="datetime-local" className={cx(inputClass, 'w-auto')} value={b} onChange={(e) => setB(e.target.value)} required />
      <Button type="submit" variant="primary">Show</Button>
      <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
    </form>
  );
}
